import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE } from "./AuthContext";
import { Sparkles, X } from "lucide-react";
import { logError } from "../lib/log";

const SHOW_WITHIN_DAYS = 5;
const DISMISS_KEY = "trial_banner_dismissed_for";

function daysBetween(iso) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  const ms = end - Date.now();
  return Math.ceil(ms / 86400000); // Round up so "ends in 0.4d" still shows "1 day"
}

export default function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/billing/subscription`)
      .then((r) => { if (!cancel) setSub(r.data); })
      .catch((e) => {
        if (e?.response?.status !== 401 && e?.response?.status !== 403) {
          logError("trial-banner:fetch", e);
        }
      });
    return () => { cancel = true; };
  }, []);

  if (!sub || sub.status !== "trialing" || !sub.trial_end) return null;
  const days = daysBetween(sub.trial_end);
  if (days === null || days > SHOW_WITHIN_DAYS) return null;
  // Per-trial dismiss — if the user dismisses, store the trial_end so a fresh trial
  // (different trial_end) will re-show the banner.
  const dismissedFor = typeof window !== "undefined" ? window.localStorage.getItem(DISMISS_KEY) : null;
  if (dismissed || dismissedFor === sub.trial_end) return null;

  const urgent = days <= 2;
  const dayLabel = days <= 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;
  const bg = urgent ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200";
  const ink = urgent ? "text-rose-900" : "text-amber-900";

  const onDismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, sub.trial_end); } catch (_e) { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 border-b ${bg} ${ink}`}
      data-testid="trial-banner"
    >
      <Sparkles size={16} className="shrink-0" />
      <div className="text-[13px] flex-1">
        <b>Trial ends {dayLabel}.</b>{" "}
        Pick a plan to keep Pro features (AI Insights, Gmail triage, practice pulse).
      </div>
      <Link
        to="/settings/billing"
        className="text-[12px] font-medium underline hover:no-underline shrink-0"
        data-testid="trial-banner-upgrade"
      >
        Pick a plan →
      </Link>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        data-testid="trial-banner-dismiss"
        className="opacity-60 hover:opacity-100 transition shrink-0"
      >
        <X size={14} />
      </button>
    </div>
  );
}
